/**
 * codeAlong · «напиши / запусти / почини» interactive engine (S13 · Block 3 debut).
 *
 * First code-practice module in the course. A SIMULATOR — the browser does not
 * execute Python; we check the student's input/choice against authored answers
 * and reveal a pre-recorded terminal output. Real execution = backend sandbox
 * (forward-hook · not now).
 *
 * Two modes via `data-mode`:
 *   - "fill"  · the student types the missing line into a blank, presses Проверить;
 *               a correct answer reveals `.ca-run-success` (simulated stdout).
 *   - "debug" · the student reads `.ca-fail-trace` and picks the right `.ca-fix`
 *               (the one with `data-ok="1"`); a correct pick reveals the разбор.
 *
 * Markup contract (authored content · degrades to readable static text without JS):
 *   <div class="code-along" data-lang="python" data-mode="fill">
 *     <div class="ca-task">условие задачи</div>
 *     <pre class="ca-code"><code>button_name = "Log in"
 *
 * <span class="ca-blank" data-answer='print(button_name)' data-alt='print( button_name )'></span></code></pre>
 *     <div class="ca-run-success">$ python hello.py
 * Log in</div>
 *     <div class="ca-hint">подсказка…</div>
 *     <details class="ca-explain"><summary>Разбор</summary><div>…</div></details>
 *   </div>
 *
 *   <div class="code-along" data-lang="python" data-mode="debug">
 *     <div class="ca-task">условие</div>
 *     <pre class="ca-code"><code>…buggy code…</code></pre>
 *     <div class="ca-fail-trace">KeyError: 'username'</div>
 *     <div class="ca-fix-options">
 *       <button type="button" class="ca-fix" data-ok="1">верный фикс</button>
 *       <button type="button" class="ca-fix">неверный</button>
 *     </div>
 *     <div class="ca-hint">подсказка…</div>
 *     <details class="ca-explain"><summary>Разбор</summary><div>…</div></details>
 *   </div>
 *
 * NOTE on the blank contract: the source content draft sketched a text marker
 * `{{blank answer="…" alt="…"}}`, but answers legitimately contain quotes
 * (`page.goto("…")`) which collide with a quoted-attribute text parser. The real
 * authored contract is a `.ca-blank` ELEMENT carrying `data-answer` / `data-alt`
 * (pipe-separated alts) — the DOM parser handles all escaping. Each S13 fill block
 * has exactly one blank.
 *
 * StrictMode-safe (React 18 dev double-invokes effects: mount → cleanup → mount):
 * the authored innerHTML is saved up-front and restored in cleanup, and the
 * `caInit` guard is deleted so the second mount can re-enhance cleanly. Mirrors
 * scDeck.ts (см. feedback_qa_studio_strictmode_interactive_modules · инцидент scDeck).
 */

import { highlight } from './codeFormat';

/** Loosely normalise a code answer for comparison: drop whitespace, unify quotes. */
function norm(s: string): string {
  return s.trim().replace(/['"]/g, '"').replace(/\s+/g, '');
}

function reveal(el: Element | null): void {
  if (el) el.classList.remove('ca-hidden');
}

/**
 * Подсветка статического кода в .ca-code: токенизируем ТЕКСТОВЫЕ узлы, элементы
 * (input/.ca-blank) не трогаем — иначе сломаем интерактив. Authored innerHTML
 * сохранён в `original` ДО вызова, поэтому cleanup восстановит чистый контент.
 */
function highlightCaCode(codeEl: HTMLElement | null, lang: 'python' | 'bash'): void {
  if (!codeEl) return;
  const texts: Text[] = [];
  codeEl.childNodes.forEach((n) => {
    if (n.nodeType === 3 && (n.textContent ?? '').trim()) texts.push(n as Text);
  });
  texts.forEach((tn) => {
    const span = document.createElement('span');
    span.innerHTML = highlight(tn.textContent ?? '', lang);
    tn.replaceWith(span);
  });
}

/** Подсветка инлайн-чипов <code> в задаче/подсказке/разборе/трейсе/фиксах. */
function tokenizeCaInline(block: HTMLElement, lang: 'python' | 'bash'): void {
  block
    .querySelectorAll<HTMLElement>(
      '.ca-task code, .ca-hint code, .ca-explain code, .ca-fix code, .ca-fail-trace code, .ca-run-success code',
    )
    .forEach((c) => {
      if (c.children.length) return;
      const t = c.textContent ?? '';
      if (t.trim()) c.innerHTML = highlight(t, lang);
    });
}

export function initCodeAlong(root: HTMLElement): () => void {
  if (typeof document === 'undefined') return () => {};
  const cleanups: Array<() => void> = [];

  const blocks = Array.from(root.querySelectorAll<HTMLElement>('.code-along'));
  for (const block of blocks) {
    if (block.dataset.caInit === '1') continue;
    block.dataset.caInit = '1';
    // Saved so cleanup can restore authored content under StrictMode double-invoke.
    const original = block.innerHTML;
    const mode = block.dataset.mode === 'debug' ? 'debug' : 'fill';
    const caLang: 'python' | 'bash' = block.dataset.lang === 'bash' ? 'bash' : 'python';

    const hint = block.querySelector<HTMLElement>('.ca-hint');
    const success = block.querySelector<HTMLElement>('.ca-run-success');

    // Hidden until earned/requested. .ca-explain (<details>) stays openable —
    // consistent with quiz .qc-explain.
    hint?.classList.add('ca-hidden');
    success?.classList.add('ca-hidden');
    block.classList.add('ca-ready');

    // Controls bar: status message + (fill) check + hint toggle.
    const bar = document.createElement('div');
    bar.className = 'ca-bar';
    const msg = document.createElement('div');
    msg.className = 'ca-msg';
    msg.setAttribute('aria-live', 'polite');

    let checkBtn: HTMLButtonElement | null = null;
    const hintBtn = hint ? document.createElement('button') : null;

    if (mode === 'fill') {
      checkBtn = document.createElement('button');
      checkBtn.type = 'button';
      checkBtn.className = 'ca-btn ca-check';
      checkBtn.textContent = 'Проверить';
      bar.appendChild(checkBtn);
    }
    if (hintBtn && hint) {
      hintBtn.type = 'button';
      hintBtn.className = 'ca-btn ca-hint-btn';
      hintBtn.textContent = 'Подсказка';
      bar.appendChild(hintBtn);
      const onHint = () => {
        reveal(hint);
        hintBtn.disabled = true;
      };
      hintBtn.addEventListener('click', onHint);
      cleanups.push(() => hintBtn.removeEventListener('click', onHint));
    }
    bar.appendChild(msg);

    if (mode === 'fill') {
      // Replace the single .ca-blank placeholder with a text input.
      const blank = block.querySelector<HTMLElement>('.ca-blank');
      const code = block.querySelector<HTMLElement>('.ca-code');
      if (!blank || !checkBtn) continue;
      const answers = [
        blank.dataset.answer ?? '',
        ...(blank.dataset.alt ? blank.dataset.alt.split('|') : []),
      ]
        .map(norm)
        .filter(Boolean);

      const input = document.createElement('input');
      input.type = 'text';
      input.className = 'ca-input';
      input.setAttribute('autocomplete', 'off');
      input.setAttribute('autocapitalize', 'off');
      input.setAttribute('spellcheck', 'false');
      input.setAttribute('aria-label', 'Допиши строку кода');
      input.placeholder = '…';
      blank.replaceWith(input);

      // Insert the bar right after the code block.
      code?.insertAdjacentElement('afterend', bar);

      let solved = false;
      const check = () => {
        if (solved) return;
        const ok = answers.includes(norm(input.value));
        if (ok) {
          solved = true;
          input.classList.remove('no');
          input.classList.add('ok');
          input.readOnly = true;
          msg.textContent = 'Верно. Запусти — вот что выведет:';
          msg.className = 'ca-msg ok';
          reveal(success);
          checkBtn!.disabled = true;
        } else {
          input.classList.add('no');
          msg.textContent = 'Не совпало. Посмотри подсказку и сверь синтаксис.';
          msg.className = 'ca-msg no';
          reveal(hint);
          if (hintBtn) hintBtn.disabled = true;
        }
      };
      const onSubmit = (e: KeyboardEvent) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          check();
        }
      };
      checkBtn.addEventListener('click', check);
      input.addEventListener('keydown', onSubmit);
      cleanups.push(() => {
        checkBtn!.removeEventListener('click', check);
        input.removeEventListener('keydown', onSubmit);
      });
    } else {
      // debug · wire .ca-fix choices.
      const fixes = Array.from(block.querySelectorAll<HTMLElement>('.ca-fix'));
      const opts = block.querySelector<HTMLElement>('.ca-fix-options');
      opts?.insertAdjacentElement('afterend', bar);
      if (!fixes.length) continue;

      let solved = false;
      for (const fix of fixes) {
        const onPick = () => {
          if (solved) return;
          const correct = fix.dataset.ok === '1';
          if (correct) {
            solved = true;
            fix.classList.add('ok');
            fixes.forEach((f) => {
              f.classList.add('lock');
              (f as HTMLButtonElement).disabled = true;
            });
            msg.textContent = 'Верно. Открой разбор ниже.';
            msg.className = 'ca-msg ok';
          } else {
            fix.classList.add('no');
            fix.classList.add('lock');
            (fix as HTMLButtonElement).disabled = true;
            msg.textContent = 'Не то — почему? Посмотри подсказку и читай трейс.';
            msg.className = 'ca-msg no';
            reveal(hint);
            if (hintBtn) hintBtn.disabled = true;
          }
        };
        fix.addEventListener('click', onPick);
        cleanups.push(() => fix.removeEventListener('click', onPick));
      }
    }

    // Подсветка кода (статический .ca-code + инлайн-чипы). После всей сборки —
    // input/ca-fix уже на месте, текстовые узлы вокруг токенизируем безопасно.
    highlightCaCode(block.querySelector<HTMLElement>('.ca-code code'), caLang);
    tokenizeCaInline(block, caLang);

    cleanups.push(() => {
      block.innerHTML = original;
      block.classList.remove('ca-ready');
      delete block.dataset.caInit;
    });
  }

  return () => cleanups.forEach((fn) => fn());
}
